import React, { useState, useEffect, useContext } from "react";
import { Navbar, Nav, Dropdown } from "react-bootstrap";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import LoginModal from "./LoginModal";
import ContentCreationWizard from "./ContentCreationWizard";
import { UserInfo } from "../helper/Context";
import { APILogout } from "../helper/APIFunctions";
import { canRequestDOI, canRestoreVisuals, canCreatePresentations, canCreateScrollytellings } from "../helper/BusinessLogic";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faMagic } from "@fortawesome/free-solid-svg-icons";
import { getUserSettings, saveUserSettings, getDefaultUserSettings } from "../helper/UserSettings";
import { generateContent } from "../helper/ContentGenerationService";

const upgradeCapsuleStyle = {
  color: 'cyan',
  fontSize: '0.8em',
  border: '1px solid cyan',
  borderRadius: '1em',
  padding: '0.2em 0.6em'
};

const UserDropdown = (props) => {
  let navigate = useNavigate();
  const isFreeTier = props.userInfo && props.userInfo.tier === 'free';


  return (
    <Dropdown>
      <Dropdown.Toggle as="div" className="nav-link">
        {props.userInfo.name}
      </Dropdown.Toggle>
      <Dropdown.Menu variant="dark" align="end">
        <Dropdown.Item  as={NavLink} to="/dashboard" role="menuitem" end>
            Dashboard
        </Dropdown.Item>
        <Dropdown.Item  as={NavLink} to="/dashboard/mylibrary" role="menuitem" end>
            My Library
        </Dropdown.Item>
        
        {/* <Dropdown.Item onClick={() => handlePremiumFeatureClick("/create-presentation")}>
          <div className="d-flex align-items-center">
            <span className="flex-grow-1 me-2">Slideshow builder</span>
            {isFreeTier && <span style={upgradeCapsuleStyle}>Upgrade</span>}
          </div>
        </Dropdown.Item>
        <Dropdown.Item onClick={() => handlePremiumFeatureClick("/scrollytelling/builder")}>
          <div className="d-flex align-items-center">
            <span className="flex-grow-1 me-2">Scrollytelling builder</span>
            {isFreeTier && <span style={upgradeCapsuleStyle}>Upgrade</span>}
          </div>
        </Dropdown.Item>
        <Dropdown.Item onClick={() => navigate("/dashboard/upload")}>
            Add New Visual
        </Dropdown.Item>
        <Dropdown.Item onClick={() => navigate('/dashboard/upload', { state: { defaultTab: 'search' } })}>
            Search (beta)
        </Dropdown.Item>

        {props.userInfo && canRestoreVisuals(props.userInfo.tier) ? (
          <Dropdown.Item onClick={() => navigate("/deletedvisuals")}>
              Deleted Visuals
          </Dropdown.Item>
        ) : null} */}

        <Dropdown.Divider />
        <Dropdown.Item  as={NavLink} to="/dashboard/settings" role="menuitem" end >
           Account Settings
        </Dropdown.Item>
        {props.userInfo?.organization_permissions ? (
          <Dropdown.Item  as={NavLink} to="/organization/info" role="menuitem" end>
            Group Info
          </Dropdown.Item>
        ) : null}
        <Dropdown.Item  as={NavLink} to="/pricing" role="menuitem" end>
            Plans
        </Dropdown.Item>
        {props.userInfo.can_add_tags ? (
          <>
            <Dropdown.Divider />
            <Dropdown.Item  as={NavLink} to="/tags" role="menuitem" end>
                Browse Visual Tags
            </Dropdown.Item>
            <Dropdown.Item  as={NavLink} to="/tags/add" role="menuitem" end>
                Add New Tags
            </Dropdown.Item>
          </>
        ) : null}
        {props.userInfo.admin ? (
          <>
            <Dropdown.Divider />
            <Dropdown.Item  as={NavLink} to="/admin" role="menuitem" end>
                Admin Options
            </Dropdown.Item>
          </>
        ) : null}
        <Dropdown.Divider />
        <Dropdown.Item
            onClick={() => {
              APILogout();
              props.setUserInfo(null);
              navigate("/home");
            }}
          >
            Logout
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

const CreateDropdown = ({ userInfo, onOpenWizard }) => {
  let navigate = useNavigate();
  const isFreeTier = userInfo && userInfo.tier === 'free';

  const handlePremiumFeatureClick = (path, predicateFunction, state ={}) => {
    if (!predicateFunction()) {
      navigate('/pricing');
    } else {
      navigate(path, {state: state});
    }
  };

  return (
    <Dropdown>
      <Dropdown.Toggle as="div" className="nav-link create-button">
        <FontAwesomeIcon icon={faPlus} className="me-2" />
        Create
      </Dropdown.Toggle>
      <Dropdown.Menu variant="dark" align="end">
        <Dropdown.Item onClick={onOpenWizard} role="menuitem">
          <FontAwesomeIcon icon={faMagic} className="me-2" />
          Content Creation Wizard
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item as={NavLink} to="/dashboard/upload" role="menuitem" end>
          Standalone Visual
        </Dropdown.Item>
        <Dropdown.Item as={NavLink} to="/create-presentation" role="menuitem" end>
          <div className="d-flex align-items-center">
            <span className="flex-grow-1 me-2">Slide Deck</span>
            {!canCreatePresentations(userInfo) && <span style={upgradeCapsuleStyle}>Upgrade</span>}
          </div>
        </Dropdown.Item>
        <Dropdown.Item as={NavLink} to="/scrollytelling/builder" role="menuitem" end>
          <div className="d-flex align-items-center">
            <span className="flex-grow-1 me-2">Scrollytelling Story</span>
            {!canCreateScrollytellings(userInfo) && <span style={upgradeCapsuleStyle}>Upgrade</span>}
          </div>
        </Dropdown.Item>
        <Dropdown.Item as={NavLink} to="/explainer" role="menuitem" end>
          Explainer Video
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

const Header = (props) => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showCreationWizard, setShowCreationWizard] = useState(false);
  const [expanded, setExpanded] = useState(false);
  
  // User settings state
  const [userSettings, setUserSettings] = useState(null);
  const { userInfo } = useContext(UserInfo);
  const location = useLocation();

  // Open wizard when navigated here with { openWizard: true } state
  useEffect(() => {
    if (location.state?.openWizard) {
      setShowCreationWizard(true);
      // Clear the state so a back-navigation doesn't re-trigger it
      window.history.replaceState({}, '');
    }
  }, [location.state]);
  
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generationError, setGenerationError] = useState(null);

  // Load user settings when userInfo changes
  useEffect(() => {
    if (!userInfo || !userInfo.email) {
      setUserSettings(null);
      return;
    }
    
    let settings = getUserSettings(userInfo.email);
    if (!settings) {
      settings = getDefaultUserSettings(userInfo);
      saveUserSettings(userInfo.email, settings);
    }
    setUserSettings(settings);
  }, [userInfo]);

  // Handle updating user settings
  const handleUpdateUserSettings = (key, value) => {
    if (!userInfo || !userInfo.email || !userSettings) return;
    
    const updatedSettings = {
      ...userSettings,
      [key]: value
    };
    
    const saved = saveUserSettings(userInfo.email, updatedSettings);
    if (saved) {
      setUserSettings(saved);
    }
  };

  // Handle content generation
  const handleGenerateContent = async (contentType) => {
    if (!userSettings) {
      setGenerationError('User settings not loaded');
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);
    setGenerationProgress(0);
    setGeneratedContent(null);
    
    try {
      const result = await generateContent(
        contentType,
        userSettings,
        (progress) => setGenerationProgress(progress)
      );
      
      setGeneratedContent(result);
    } catch (error) {
      console.error('Content generation error:', error);
      setGenerationError(error.message || 'Failed to generate content');
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  // Reset generation state
  const handleResetWizard = () => {
    setGeneratedContent(null);
    setGenerationError(null);
    setGenerationProgress(0);
  };

  const handleNavClick = () => {
    setExpanded(false);
  };

  return (
    <>
      <Navbar
        collapseOnSelect
        expand="lg"
        variant="dark"
        className="bg-tertiary mt-0 px-4"
        expanded={expanded}
        onToggle={(expanded) => setExpanded(expanded)}
      >
        <NavLink
          className="navbar-brand visualization-link me-4 d-flex align-items-center"
          to="/home"
          onClick={handleNavClick}
        >
          {/* <img
            src="/portal/new_snorkle_icon1.png"
            height="30px"
            width="30px"
            className="me-2 border border-light rounded"
            alt="Snorkle"
          /> */}
          <h1 className="mb-0" style={{ color: "#EA088B" }} >Snorkle</h1>
          <sup>
            <i>
              <span style={{ color: "cyan", fontWeight: "bold" }}>Labs</span>
            </i>
          </sup>
        </NavLink>
        <Navbar.Toggle aria-controls="responsive-navbar-nav" />
        <Navbar.Collapse id="responsive-navbar-nav">
          <Nav>
            <UserInfo.Consumer>
              {({ userInfo }) =>
                userInfo !== null && (
                  <>
                    <NavLink className="nav-link" to="/dashboard" end onClick={handleNavClick}>
                      Dashboard
                    </NavLink>
                    <NavLink className="nav-link" to="/dashboard/myvisuals" onClick={handleNavClick}>
                      My Library
                    </NavLink>
                  </>
                )
              }
            </UserInfo.Consumer>
            {/* <NavLink className="nav-link" to="/about">
              About
            </NavLink> */}
            <UserInfo.Consumer>
              {({ userInfo }) =>
                (userInfo === null || (userInfo && userInfo.tier === "free")) && (
                  <NavLink className="nav-link" to="/pricing" onClick={handleNavClick}>
                    Plans
                  </NavLink>
                )
              }
            </UserInfo.Consumer>
            <NavLink className="nav-link" to="/powerpoint" onClick={handleNavClick}>
              PowerPoint Add-in
            </NavLink>
            {/* <NavLink className="nav-link" to="/tutorials">
              Tutorials
            </NavLink> */}
            <NavLink className="nav-link" to="/contact#contact-form-top" onClick={handleNavClick}>
              Contact Us
            </NavLink>
            {/* <NavLink className="nav-link" to="/bundles">
              Premium Bundles
            </NavLink>
            <NavLink className="nav-link" to="/special/gnydm2023">
              Special Events
            </NavLink> */}

            {/* <NavLink className="nav-link" to="/visuals">
              Browse Visuals
            </NavLink> */}
          </Nav>
          <Nav className="ms-auto">
            <UserInfo.Consumer>
              {({ userInfo, setUserInfo }) => {
                return userInfo !== null ? (
                  <>
                    <CreateDropdown 
                      userInfo={userInfo} 
                      onOpenWizard={() => setShowCreationWizard(true)}
                    />
                    <UserDropdown userInfo={userInfo} setUserInfo={setUserInfo} />
                  </>
                ) : (
                  <div
                    className="nav-link"
                    onClick={() => {
                      setShowLoginModal(true);
                      handleNavClick();
                    }}
                  >
                    Login / Register
                  </div>
                );
              }}
            </UserInfo.Consumer>
          </Nav>
        </Navbar.Collapse>
      </Navbar>
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
      {userSettings && (
        <ContentCreationWizard
          show={showCreationWizard}
          onHide={() => {
            setShowCreationWizard(false);
            handleResetWizard();
          }}
          userSettings={userSettings}
          onUpdateUserSettings={handleUpdateUserSettings}
          onGenerate={handleGenerateContent}
          isGenerating={isGenerating}
          generationProgress={generationProgress}
          generatedContent={generatedContent}
          generationError={generationError}
          onReset={handleResetWizard}
          onOpenManualPicker={() => {}}
          visuals={[]}
          presentations={[]}
          explainers={[]}
          videos={[]}
          fromChannel={false}
        />
      )}
    </>
  );
};

export default Header;
